import { Vec2 } from './vec';
import { Rng } from './rng';

/**
 * Press targets an enemy drops on the playfield: Chiply's Dad's chips and
 * The Lifeguard's whistles (spec 2026-09-09-moon-stages-design.md §2).
 *
 * Every `chipDrop.cooldown` seconds a target lands somewhere the player can
 * reach it; its fuse burns for `chipDrop.fuse` seconds and, unpressed, it
 * costs lives. The render layer draws it as a medium sprite labelled
 * "Press" reddening as `remaining` falls, and dispatches `pressChip` on a
 * tap. Purely sim-owned so a run is deterministic under a seed.
 *
 * `kind` picks the sprite and the miss overlay (K.O for a chip, STRIKE for a
 * whistle); `owner` is the carrier, so an `exclusive` carrier (the
 * Lifeguard) drops nothing while one of its own targets is still up, and a
 * dead carrier's targets vanish with it.
 */
export type ChipKind = 'chip' | 'whistle' | 'skyshroom';

export interface ChipState {
  id: number;
  kind: ChipKind;
  /** Enemy id of the carrier that dropped it. */
  owner: number;
  pos: Vec2;
  /** Seconds left before the chip costs a life. */
  remaining: number;
  /** The full fuse, so the render layer can scale the warning tint. */
  fuse: number;
  /** Lives an unpressed chip costs (the drop spec's `lives`). */
  lives: number;
  /**
   * King Shroometron's falling mushrooms (`skyshroom`): instead of lives, one
   * that reaches the bottom hits a random living tower for this much HP. The
   * render layer draws it falling from the top to the bottom over its fuse,
   * at `pos.x`.
   */
  towerDamage?: number;
}

/**
 * Where a chip may land: inside the visible playfield, under the top bar and
 * well left of the HUD's tower panel (x >= 1070, where the loadout is
 * picked) — an 88px chip centred at x1 ends at 944, so no chip is ever
 * behind the panel or in reach of it. Margins keep the sprite unclipped.
 */
/**
 * Where a press target may land. `y0` is 150, not 110, because the level-start
 * notice is an INTERACTIVE panel spanning y 61..139 (GameScene.showLevelNotice)
 * and it swallows the tap meant for anything under it: a chip that landed in
 * that band could not be pressed at all, and cost two lives for nothing. The
 * notice only shows for the first eight seconds, but a boss can drop inside
 * them, so the fix is to keep the whole class of overlap impossible.
 */
export const CHIP_AREA = { x0: 90, x1: 900, y0: 150, y1: 640 };

/**
 * A chip keeps this far (px) from every plot — an 88px chip must never
 * overlap a plot or the tower on it (the biggest tower sprite is ~64px:
 * 44 + 32 = 76), so a tap on it never lands on the loadout — and this far
 * from the trail's centreline (it may overlap the trail's shoulder;
 * enemies walk under it).
 */
export const CHIP_PLOT_CLEARANCE = 90;
export const CHIP_TRAIL_CLEARANCE = 40;

function segDist(p: Vec2, a: Vec2, b: Vec2): number {
  const vx = b.x - a.x;
  const vy = b.y - a.y;
  const len2 = vx * vx + vy * vy;
  let t = len2 ? ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
}

/** Whether a point is clear of every plot and every trail segment. */
export function chipSpotClear(p: Vec2, path: Vec2[], plots: Vec2[]): boolean {
  for (const pl of plots) {
    if (Math.hypot(p.x - pl.x, p.y - pl.y) < CHIP_PLOT_CLEARANCE) return false;
  }
  for (let i = 1; i < path.length; i++) {
    if (segDist(p, path[i - 1], path[i]) < CHIP_TRAIL_CLEARANCE) return false;
  }
  return true;
}

/** Distance from a point to the nearest plot (Infinity with no plots). */
function nearestPlot(p: Vec2, plots: Vec2[]): number {
  let best = Infinity;
  for (const pl of plots) best = Math.min(best, Math.hypot(p.x - pl.x, p.y - pl.y));
  return best;
}

/**
 * Roll a landing spot from the sim's seeded RNG: up to 32 tries for a spot
 * clear of the plots and the trail, else the try farthest from any plot (a
 * crowded map still gets its chip — over the trail at worst, never over a
 * tower, so it can always be pressed).
 */
export function rollChipPos(rng: Rng, path: Vec2[], plots: Vec2[]): Vec2 {
  let best: Vec2 = { x: CHIP_AREA.x0, y: CHIP_AREA.y0 };
  let bestDist = -Infinity;
  for (let i = 0; i < 32; i++) {
    const p = {
      x: Math.round(CHIP_AREA.x0 + rng.next() * (CHIP_AREA.x1 - CHIP_AREA.x0)),
      y: Math.round(CHIP_AREA.y0 + rng.next() * (CHIP_AREA.y1 - CHIP_AREA.y0)),
    };
    if (chipSpotClear(p, path, plots)) return p;
    const d = nearestPlot(p, plots);
    if (d > bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best;
}
